import api from "../utils/api";
import type {
  Order,
  OrderAbandonedResponse,
  OrderFilters,
  OrderListResponse,
  OrderRefundResponse,
  OrderStatsResponse,
} from "../types/order";

/**
 * Admin Orders service.
 *
 * Wraps the `/api/admin/orders` endpoints from Phases 3-4 of the
 * shopping-cart implementation plan.
 *
 * The bare `api` client (`utils/api.js`) is untyped, so we cast the
 * resolved payload to the appropriate response shape at the boundary.
 */
export const orderService = {
  /**
   * Paginated, filterable list of admin orders.
   * @param params - Optional filters (status, gateway, userId, couponCode, page, limit).
   */
  list: (params: OrderFilters = {}): Promise<OrderListResponse> => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        qs.append(key, String(value));
      }
    });
    const query = qs.toString();
    return api.get(
      `/api/admin/orders${query ? `?${query}` : ""}`,
    ) as Promise<OrderListResponse>;
  },

  /**
   * Fetch a single order with populated user and enrollments.
   * @param id - Order id.
   */
  get: (id: string): Promise<{ status: string; data: { order: Order } }> =>
    api.get(
      `/api/admin/orders/${id}`,
    ) as Promise<{ status: string; data: { order: Order } }>,

  /**
   * Issue an admin-initiated refund against a paid order.
   * Refunds the entire order (and revokes related enrollments) atomically.
   * @param id - Order id.
   * @param reason - Optional admin note describing the refund.
   */
  refund: (id: string, reason: string): Promise<OrderRefundResponse> =>
    api.post(
      `/api/admin/orders/${id}/refund`,
      { reason },
    ) as Promise<OrderRefundResponse>,

  /**
   * Pending orders that have been awaiting payment for more than one hour.
   */
  abandoned: (): Promise<OrderAbandonedResponse> =>
    api.get(
      `/api/admin/orders/abandoned`,
    ) as Promise<OrderAbandonedResponse>,

  /**
   * Order counts by status plus total revenue (USD).
   */
  stats: (): Promise<OrderStatsResponse> =>
    api.get(
      `/api/admin/orders/stats`,
    ) as Promise<OrderStatsResponse>,
};

export default orderService;
